/**
 * TypeScript bridge for the Rust/WASM physics kernel.
 * Handles loading the WASM module and packing TS data into typed arrays.
 *
 * Usage (from botWorker.ts):
 *   await initWasm();
 *   if (isWasmReady()) {
 *     setPlanets(planets);
 *     setContext(player, targetPos, targetPlanetIndex, effectiveRadius, allPlayers);
 *     const scores = scoreBatch(candidates);
 *   }
 */

import type { PlanetState, PlayerState, Vec3, ProjectileSimResult } from '../types';
import type { AimCandidate, BatchScorer, ScoringContext } from './bot';
import { WEAPONS } from '../constants';
import type { GeyserSim, HitboxSim } from '../core/flight';
import { losEyePoint } from '../core/los';

// Dynamic import types from wasm-pack generated bindings
type WasmModule = {
  default: (input?: unknown) => Promise<unknown>;
  initSync: (input: { module: BufferSource }) => unknown;
  clear_planets: (count: number) => void;
  set_planet: (meta: Float64Array, heightmap: Float32Array, basePositions: Float32Array) => void;
  set_context: (data: Float64Array) => void;
  set_wind_grids: (n: number, data: Float64Array) => void;
  set_wind_gusts: (data: Float64Array) => void;
  score_batch: (candidates: Float64Array) => Float64Array;
  create_crater_wasm: (
    planet_index: number,
    impact_x: number, impact_y: number, impact_z: number,
    blast_radius: number, depth_multiplier: number,
    density: number, initial_mass: number,
  ) => number;
  get_crater_heightmap: () => Float32Array;
  get_crater_modified: () => Uint32Array;
  calculate_player_los: (
    px: number, py: number, pz: number,
    playerPlanetIdx: number, targetPlanetIdx: number,
  ) => Uint32Array;
  get_visible_enemies: (
    vx: number, vy: number, vz: number,
    viewerPlanetIdx: number, enemyData: Float64Array,
  ) => Int32Array;
  count_enemy_los: (
    cx: number, cy: number, cz: number,
    candidatePlanetIdx: number, enemyData: Float64Array,
  ) => number;
  clear_geysers: () => void;
  set_geyser: (data: Float64Array) => void;
  simulate_projectile: (
    pos_x: number, pos_y: number, pos_z: number,
    vel_x: number, vel_y: number, vel_z: number,
    max_steps: number, out_of_bounds: number,
    cooldown_planet_idx: number,
  ) => Float64Array;
  set_hitboxes: (data: Float64Array) => void;
  update_planet_terrain: (planet_index: number, heightmap: Float32Array, mass: number) => void;
  set_planet_alive: (planet_index: number, alive: boolean) => void;
  simulate_trajectory_hit: (
    pos_x: number, pos_y: number, pos_z: number,
    vel_x: number, vel_y: number, vel_z: number,
    max_steps: number, out_of_bounds: number, dt: number,
  ) => Float64Array;
  simulate_trajectory_hit_batch: (
    trajectories: Float64Array,
    max_steps: number, out_of_bounds: number, dt: number,
  ) => Float64Array;
  simulate_jump_batch: (
    trajectories: Float64Array,
    target_planet_index: number,
    max_steps: number, out_of_bounds: number, dt: number,
  ) => Float64Array;
  create_world: () => number;
  select_world: (id: number) => void;
  drop_world: (id: number) => void;
  current_world: () => number;
  world_count: () => number;
};

let wasmModule: WasmModule | null = null;
let ready = false;
let enabled = true;

/**
 * Game-logic switch. In a P2P match the host decides whether BOTH peers use
 * the kernel (only if both have it); when disabled every wrapper returns null
 * and callers fall back to the identical JS implementation.
 */
export function setWasmEnabled(on: boolean): void {
  enabled = on;
}
export function isWasmEnabled(): boolean {
  return enabled;
}

/** The module, or null when not loaded or disabled for game logic. */
const wasmRef = {
  get current(): WasmModule | null { return enabled ? wasmModule : null; },
};

/**
 * Load and initialize the WASM module.
 * Returns true if successful, false if WASM is unavailable.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
let initPromise: Promise<boolean> | null = null;
export function initWasm(): Promise<boolean> {
  // One load for every caller: two concurrent inits each instantiated the
  // module, and the second could wipe planets the first had already uploaded
  // (an `unreachable` panic in the next LOS call).
  initPromise ??= loadWasm();
  return initPromise;
}
async function loadWasm(): Promise<boolean> {
  if (ready) return true;
  try {
    // Import the wasm-pack generated JS module.
    // The .wasm file is loaded relative to the JS file.
    const mod = await import('../../wasm-physics/pkg/orbituary_physics.js') as unknown as WasmModule;
    await mod.default();
    wasmModule = mod;
    ready = true;
    return true;
  } catch (_e) {
    // WASM unavailable — will fall back to JS scorer
    ready = false;
    return false;
  }
}

/**
 * Synchronous load from the .wasm bytes, for Node (the headless harness):
 * the web loader fetches the file by URL, which Node cannot do for file://.
 */
export async function initWasmFromBytes(bytes: BufferSource): Promise<boolean> {
  if (ready) return true;
  try {
    const mod = await import('../../wasm-physics/pkg/orbituary_physics.js') as unknown as WasmModule;
    mod.initSync({ module: bytes });
    wasmModule = mod;
    ready = true;
    return true;
  } catch (_e) {
    ready = false;
    return false;
  }
}

/** Loaded AND enabled for game logic. */
export function isWasmReady(): boolean {
  return ready && enabled;
}

/** Loaded, regardless of the game-logic switch (capability handshake). */
export function isWasmLoaded(): boolean {
  return ready;
}

// ---- Kernel worlds (one per match; ARCHITECTURE §8 step 2, world isolation) ---------------
// The kernel's planets, hitboxes, geysers and search context live in a world, and every call
// reads the selected one. World 0 always exists: a host that never creates one (the game, the bot
// worker) runs exactly as before. Two matches in one realm each own a world and select it before
// they touch the kernel (Match.enterKernel), so interleaving at an await can't cross their state.

/** The default world: always alive, never dropped. */
export const DEFAULT_KERNEL_WORLD = 0;

/**
 * 71.4 step 1: the heights each world's planets hold, as last sent (setPlanets, updatePlanetTerrain, a kernel crater), so
 * the match can tell when its coarse ground (core/coarseLift.ts coarseHeights) must be sent again.
 */
const heldHeights = new Map<number, Array<Float32Array | undefined>>();
function holdHeights(planetIndex: number, heights: Float32Array): void {
  const world = wasmModule ? wasmModule.current_world() : DEFAULT_KERNEL_WORLD;
  let list = heldHeights.get(world);
  if (!list) { list = []; heldHeights.set(world, list); }
  list[planetIndex] = heights;
}
/** The heights the selected world holds for `planetIndex`, as last sent (undefined: none known). */
export function kernelHeights(planetIndex: number): Float32Array | undefined {
  return heldHeights.get(wasmModule ? wasmModule.current_world() : DEFAULT_KERNEL_WORLD)?.[planetIndex];
}


/** A new, empty world (fill it with setPlanets after selecting it); the default world when the kernel isn't loaded. */
export function createKernelWorld(): number {
  if (!wasmModule) return DEFAULT_KERNEL_WORLD;
  const id = wasmModule.create_world();
  heldHeights.delete(id);   // (71.4: an id may be one dropped before)
  return id;
}
/** Make `id` the world every later call reads. A no-op when the kernel isn't loaded or it is already selected. */
export function selectKernelWorld(id: number): void {
  if (!wasmModule || wasmModule.current_world() === id) return;
  wasmModule.select_world(id);
}
/** Free a world (the default world is never dropped). */
export function dropKernelWorld(id: number): void {
  if (wasmModule && id !== DEFAULT_KERNEL_WORLD) { wasmModule.drop_world(id); heldHeights.delete(id); }
}
/** Live worlds, the default included (0 when the kernel isn't loaded): for leak tests. */
export function kernelWorldCount(): number {
  return wasmModule ? wasmModule.world_count() : 0;
}

/**
 * Send all planet data to WASM. Call once per turn before scoring.
 */
export function setPlanets(planets: PlanetState[]): void {
  const wasm = wasmRef.current;
  if (!wasm) return;
  heldHeights.delete(wasmModule ? wasmModule.current_world() : DEFAULT_KERNEL_WORLD);

  wasm.clear_planets(planets.length);

  const meta = new Float64Array(7);
  for (const planet of planets) {
    meta[0] = planet.position.x;
    meta[1] = planet.position.y;
    meta[2] = planet.position.z;
    meta[3] = planet.radius;
    meta[4] = planet.mass;
    meta[5] = planet.alive ? 1.0 : 0.0;
    meta[6] = planet.density;
    wasm.set_planet(meta, planet.heightmap, planet.basePositions);
    holdHeights(planets.indexOf(planet), planet.heightmap);
  }
}

/**
 * Decision 120 / 62.54 step 4: the selected world's wind grids (core/windField.ts windGrids: the field sampled on each
 * planet's cube-sphere nodes, `n` cells a face edge). Lobbed shells in every kernel flight (the game's, the fire
 * search's) read them; jumps and bullets do not. Null turns the wind off. Resets the gusts: set them after.
 */
export function setWindGrids(grids: { n: number; data: Float64Array } | null): void {
  const wasm = wasmRef.current;
  if (!wasm) return;
  wasm.set_wind_grids(grids ? grids.n : 0, grids ? grids.data : new Float64Array(0));
}

/** 62.54 step 4: the gust now, per planet: [strength, cos(drift), sin(drift)] (core/windField.ts windGusts). */
export function setWindGusts(gusts: Float64Array): void {
  const wasm = wasmRef.current;
  if (!wasm) return;
  wasm.set_wind_gusts(gusts);
}

/**
 * Set scoring context for the upcoming scoreBatch call.
 */
export function setContext(
  player: PlayerState,
  planets: PlanetState[],
  targetPos: Vec3,
  targetPlanetIndex: number,
  effectiveRadius: number,
  allPlayers?: PlayerState[],
  fuelDuration?: number,
  maxSteps = 7200,
  escapeTest = true,
): void {
  const wasm = wasmRef.current;
  if (!wasm) return;

  // Find teammate planet indices for friendly fire avoidance
  const teammatePlanetIndices: number[] = [];
  if (allPlayers) {
    for (const p of allPlayers) {
      if (p.id !== player.id && p.alive && p.teamIndex === player.teamIndex) {
        if (!teammatePlanetIndices.includes(p.planetIndex)) {
          teammatePlanetIndices.push(p.planetIndex);
        }
      }
    }
  }

  const data = new Float64Array(13 + teammatePlanetIndices.length);
  data[0] = player.planetIndex;
  data[1] = player.surfaceDir.x;
  data[2] = player.surfaceDir.y;
  data[3] = player.surfaceDir.z;
  data[4] = targetPos.x;
  data[5] = targetPos.y;
  data[6] = targetPos.z;
  data[7] = targetPlanetIndex;
  data[8] = effectiveRadius;
  data[9] = teammatePlanetIndices.length;
  data[10] = fuelDuration ?? 1.5;
  for (let i = 0; i < teammatePlanetIndices.length; i++) {
    data[11 + i] = teammatePlanetIndices[i];
  }
  // 37.19: search cap and escape test ride after the teammate list (older layouts default to 7200 / on)
  data[11 + teammatePlanetIndices.length] = maxSteps;
  data[12 + teammatePlanetIndices.length] = escapeTest ? 1 : 0;

  wasm.set_context(data);
}

/**
 * A fire-search batch scorer backed by the kernel, or undefined when it is not
 * ready (callers fall back to the JS scorer). Pushes the planets now; the
 * scoring context is set on the first batch, once the weapon is chosen.
 */
export function buildWasmScorer(planets: PlanetState[]): BatchScorer | undefined {
  if (!isWasmReady()) return undefined;
  setPlanets(planets);
  let contextKey: string | null = null;
  return (candidates: AimCandidate[], ctx: ScoringContext) => {
    // The context is set once per weapon, and again when the flight cap changes (finalists re-fly at full length)
    const key = `${ctx.weaponKey}|${ctx.maxSteps ?? 7200}|${ctx.escapeTest ?? true}`;
    if (contextKey !== key) {
      const weapon = WEAPONS[ctx.weaponKey];
      setContext(ctx.player, ctx.planets, ctx.targetPos, ctx.targetPlanetIndex,
        weapon.blastRadius * 2.2, ctx.allPlayers, weapon.fuelDuration, ctx.maxSteps ?? 7200, ctx.escapeTest ?? true);
      contextKey = key;
    }
    scoreBatch(candidates);
  };
}

/**
 * Score a batch of aim candidates via WASM.
 * Writes scores directly onto the candidate objects.
 */
export function scoreBatch(candidates: AimCandidate[]): void {
  const wasm = wasmRef.current;
  if (!wasm) return;

  const count = candidates.length;
  const flat = new Float64Array(count * 3);
  for (let i = 0; i < count; i++) {
    flat[i * 3] = candidates[i].azimuth;
    flat[i * 3 + 1] = candidates[i].elevation;
    flat[i * 3 + 2] = candidates[i].power;
  }

  const scores = wasm.score_batch(flat);
  for (let i = 0; i < count; i++) {
    candidates[i].score = scores[i];
  }
}

export interface WasmCraterResult {
  newHeightmap: Float32Array;
  modifiedVertices: Uint32Array;
  newMass: number;
}

/**
 * Create a crater via WASM. Returns null if WASM not ready.
 */
export function createCraterWasm(
  planetIndex: number,
  impactPoint: Vec3,
  blastRadius: number,
  depthMultiplier: number,
  density: number,
  initialMass: number,
): WasmCraterResult | null {
  const wasm = wasmRef.current;
  if (!wasm) return null;

  const newMass = wasm.create_crater_wasm(
    planetIndex,
    impactPoint.x, impactPoint.y, impactPoint.z,
    blastRadius, depthMultiplier,
    density, initialMass,
  );

  const newHeightmap = wasm.get_crater_heightmap() as Float32Array;
  holdHeights(planetIndex, newHeightmap);
  const modifiedVertices = wasm.get_crater_modified() as Uint32Array;

  return { newHeightmap, modifiedVertices, newMass };
}

// =============================================================================
// Full Projectile Simulation — WASM wrappers
// =============================================================================

/** Push one planet's new heightmap + mass (no index rebuild). */
export function updatePlanetTerrain(planetIndex: number, heightmap: Float32Array, mass: number): void {
  const wasm = wasmRef.current;
  if (!wasm) return;
  wasm.update_planet_terrain(planetIndex, heightmap, mass);
  holdHeights(planetIndex, heightmap);
}

/** Mark a planet alive/dead in WASM (planet destruction). */
export function setPlanetAlive(planetIndex: number, alive: boolean): void {
  const wasm = wasmRef.current;
  if (!wasm) return;
  wasm.set_planet_alive(planetIndex, alive);
}

/**
 * Send geyser boost sources for the next simulateProjectileFull call.
 * Build them with core/flight buildGeyserSims so JS and WASM see identical data.
 */
export function setGeysers(geysers: GeyserSim[]): void {
  const wasm = wasmRef.current;
  if (!wasm) return;
  wasm.clear_geysers();
  const data = new Float64Array(7);
  for (const g of geysers) {
    data[0] = g.worldPos.x; data[1] = g.worldPos.y; data[2] = g.worldPos.z;
    data[3] = g.normal.x; data[4] = g.normal.y; data[5] = g.normal.z;
    data[6] = g.id;
    wasm.set_geyser(data);
  }
}

/** Send player hitboxes for the next simulateProjectileFull call (core/flight buildHitboxes). */
export function setHitboxes(hitboxes: HitboxSim[]): void {
  const wasm = wasmRef.current;
  if (!wasm) return;
  const data = new Float64Array(hitboxes.length * 8);
  for (let i = 0; i < hitboxes.length; i++) {
    const h = hitboxes[i];
    const b = i * 8;
    data[b] = h.center.x; data[b + 1] = h.center.y; data[b + 2] = h.center.z;
    data[b + 3] = h.surfacePoint.x; data[b + 4] = h.surfacePoint.y; data[b + 5] = h.surfacePoint.z;
    data[b + 6] = h.planetIndex; data[b + 7] = h.playerId;
  }
  wasm.set_hitboxes(data);
}

/**
 * Simulate one projectile flight segment via WASM (mirror of simulateFlightSegment).
 * Returns null if WASM not ready.
 */
export function simulateProjectileFull(
  startPos: Vec3,
  startVel: Vec3,
  maxSteps: number,
  outOfBounds: number,
  cooldownPlanetIndex = -1,
): ProjectileSimResult | null {
  const wasm = wasmRef.current;
  if (!wasm) return null;

  const raw = wasm.simulate_projectile(
    startPos.x, startPos.y, startPos.z,
    startVel.x, startVel.y, startVel.z,
    maxSteps, outOfBounds, cooldownPlanetIndex,
  );

  const numPoints = raw[0];
  const hitPlanetIndex = raw[1];
  const hitPoint = hitPlanetIndex >= 0 ? { x: raw[2], y: raw[3], z: raw[4] } : null;
  const finalVel = { x: raw[5], y: raw[6], z: raw[7] };
  const numGeyserEvents = raw[8];
  const hitPlayerId = raw[9];

  const geyserEvents: { step: number; geyserId: number }[] = [];
  for (let i = 0; i < numGeyserEvents; i++) {
    geyserEvents.push({ step: raw[10 + i * 2], geyserId: raw[10 + i * 2 + 1] });
  }

  const headerEnd = 10 + numGeyserEvents * 2;
  const points: Vec3[] = new Array(numPoints);
  for (let i = 0; i < numPoints; i++) {
    const o = headerEnd + i * 3;
    points[i] = { x: raw[o], y: raw[o + 1], z: raw[o + 2] };
  }

  return { points, hitPlanetIndex, hitPoint, finalVel, geyserEvents, hitPlayerId };
}


// =============================================================================
// Lean Trajectory Simulation — collision result only (no trajectory points)
// =============================================================================

export interface TrajectoryHitResult {
  planetIndex: number;  // -1 if OOB/timeout
  point: Vec3;
}

/**
 * Simulate trajectory and return only collision info. Returns null if WASM not ready.
 * Used for backtrackFireSource and simulateJumpTrajectory (no points needed).
 */
export function wasmSimulateTrajectoryHit(
  startPos: Vec3,
  startVel: Vec3,
  maxSteps: number,
  outOfBounds: number,
  dt: number,
): TrajectoryHitResult | null {
  const wasm = wasmRef.current;
  if (!wasm) return null;

  const raw = wasm.simulate_trajectory_hit(
    startPos.x, startPos.y, startPos.z,
    startVel.x, startVel.y, startVel.z,
    maxSteps, outOfBounds, dt,
  );

  return {
    planetIndex: raw[0],
    point: { x: raw[1], y: raw[2], z: raw[3] },
  };
}

/**
 * Batch simulate trajectories — collision results only. Returns null if WASM not ready.
 * Input: array of {pos, vel} pairs. Returns array of TrajectoryHitResult.
 */
export function wasmSimulateTrajectoryHitBatch(
  trajectories: Array<{ pos: Vec3; vel: Vec3 }>,
  maxSteps: number,
  outOfBounds: number,
  dt: number,
): TrajectoryHitResult[] | null {
  const wasm = wasmRef.current;
  if (!wasm) return null;

  const count = trajectories.length;
  const flat = new Float64Array(count * 6);
  for (let i = 0; i < count; i++) {
    const t = trajectories[i];
    flat[i * 6] = t.pos.x;
    flat[i * 6 + 1] = t.pos.y;
    flat[i * 6 + 2] = t.pos.z;
    flat[i * 6 + 3] = t.vel.x;
    flat[i * 6 + 4] = t.vel.y;
    flat[i * 6 + 5] = t.vel.z;
  }

  const raw = wasm.simulate_trajectory_hit_batch(flat, maxSteps, outOfBounds, dt);

  const results: TrajectoryHitResult[] = [];
  for (let i = 0; i < count; i++) {
    results.push({
      planetIndex: raw[i * 4],
      point: { x: raw[i * 4 + 1], y: raw[i * 4 + 2], z: raw[i * 4 + 3] },
    });
  }
  return results;
}

export interface JumpBatchResult {
  landingPlanetIndex: number;  // -1 if OOB/timeout
  closestApproach: number;     // 0 if landed on target, else min distance to target surface
}

/**
 * Batch simulate jump trajectories with closest-approach tracking.
 * Returns null if WASM not ready.
 * Input: array of {pos, vel} pairs + target planet index.
 * Returns array of JumpBatchResult (landing planet + closest approach distance).
 */
export function wasmSimulateJumpBatch(
  trajectories: Array<{ pos: Vec3; vel: Vec3 }>,
  targetPlanetIndex: number,
  maxSteps: number,
  outOfBounds: number,
  dt: number,
): JumpBatchResult[] | null {
  const wasm = wasmRef.current;
  if (!wasm) return null;

  const count = trajectories.length;
  const flat = new Float64Array(count * 6);
  for (let i = 0; i < count; i++) {
    const t = trajectories[i];
    flat[i * 6] = t.pos.x;
    flat[i * 6 + 1] = t.pos.y;
    flat[i * 6 + 2] = t.pos.z;
    flat[i * 6 + 3] = t.vel.x;
    flat[i * 6 + 4] = t.vel.y;
    flat[i * 6 + 5] = t.vel.z;
  }

  const raw = wasm.simulate_jump_batch(flat, targetPlanetIndex, maxSteps, outOfBounds, dt);

  const results: JumpBatchResult[] = [];
  for (let i = 0; i < count; i++) {
    results.push({
      landingPlanetIndex: raw[i * 2],
      closestApproach: raw[i * 2 + 1],
    });
  }
  return results;
}

// =============================================================================
// LOS Raycasting — WASM wrappers
// =============================================================================

/**
 * Calculate visible vertices via WASM. Returns null if WASM not ready.
 */
export function wasmCalculatePlayerLOS(
  playerPos: Vec3,
  playerPlanetIndex: number,
  targetPlanetIndex: number,
): number[] | null {
  const wasm = wasmRef.current;
  if (!wasm) return null;
  const raw = wasm.calculate_player_los(
    playerPos.x, playerPos.y, playerPos.z,
    playerPlanetIndex, targetPlanetIndex,
  );
  // Convert Uint32Array to number[]
  const result: number[] = new Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    result[i] = raw[i];
  }
  return result;
}

/**
 * Get visible enemies via WASM. Returns null if WASM not ready.
 * Packs enemy data into flat Float64Array: [pos_x, pos_y, pos_z, planetIndex, id] × N
 */
export function wasmGetVisibleEnemies(
  viewerPos: Vec3,
  viewerPlanetIndex: number,
  enemies: PlayerState[],
  planets: PlanetState[],
): number[] | null {
  const wasm = wasmRef.current;
  if (!wasm) return null;

  // Pack enemy data
  const enemyData = new Float64Array(enemies.length * 5);
  for (let i = 0; i < enemies.length; i++) {
    const enemy = enemies[i];
    const pos = getEnemyWorldPos(enemy, planets);
    enemyData[i * 5] = pos.x;
    enemyData[i * 5 + 1] = pos.y;
    enemyData[i * 5 + 2] = pos.z;
    enemyData[i * 5 + 3] = enemy.planetIndex;
    enemyData[i * 5 + 4] = enemy.id;
  }

  const raw = wasm.get_visible_enemies(
    viewerPos.x, viewerPos.y, viewerPos.z,
    viewerPlanetIndex, enemyData,
  );

  const result: number[] = new Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    result[i] = raw[i];
  }
  return result;
}

/**
 * Count enemy LOS via WASM. Returns null if WASM not ready.
 * Packs enemy data into flat Float64Array: [pos_x, pos_y, pos_z, planetIndex] × N
 */
export function wasmCountEnemyLOS(
  candidatePos: Vec3,
  candidatePlanetIndex: number,
  enemies: PlayerState[],
  planets: PlanetState[],
): number | null {
  const wasm = wasmRef.current;
  if (!wasm) return null;

  const enemyData = new Float64Array(enemies.length * 4);
  for (let i = 0; i < enemies.length; i++) {
    const enemy = enemies[i];
    const pos = getEnemyWorldPos(enemy, planets);
    enemyData[i * 4] = pos.x;
    enemyData[i * 4 + 1] = pos.y;
    enemyData[i * 4 + 2] = pos.z;
    enemyData[i * 4 + 3] = enemy.planetIndex;
  }

  return wasm.count_enemy_los(
    candidatePos.x, candidatePos.y, candidatePos.z,
    candidatePlanetIndex, enemyData,
  );
}

/** A player's eye point — the LOS endpoint (core/los.ts). */
const getEnemyWorldPos = losEyePoint;
